import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { findUserShowRatingByListingId, getUserShowRatings } from '../../util/data';
import { useElementSize } from '../../util/hooks';
import { roleHasPermission } from '../../util/orgRoles';
import {
  ResponsiveImage,
  Modal,
  ImageCarousel,
  NamedLink,
  InlineTextButton,
  ExpandableBio,
  ButtonWatchlist,
  AspectRatioWrapper,
  Icon,
} from '../../components';
import classNames from 'classnames';

import css from './ListingPage.module.css';
import { createSlug } from '../../util/urlHelpers';

const MAX_AVERAGE_REVIEW = 10;
const DESCRIPTION_MAX_LENGTH = 130;
const DESCRIPTION_MAX_LENGTH_MOBILE = 60;

const getPosterImage = (listing, posterImageId, firstImage) => {
  const posterImage = listing?.images?.find(i => i.id.uuid === posterImageId) ?? firstImage;
  return posterImage;
};

const SectionImages = props => {
  const {
    title,
    richTitle,
    listing,
    currentUser,
    reviews,
    averageReview,
    organizationProfile,
    organizationProfiles,
    posterImageId,
    location,
    isVariant,
    isOwnListing,
    isMobileLayout,
    isTabletLayout,
    imageCarouselOpen,
    activeImageIndex,
    editParams,
    editMessageId,
    handleViewPhotosClick,
    onImageCarouselClose,
    onOpenShareModal,
    onOpenShowRatingModal,
    onOpenDescriptionModal,
    onManageDisableScrolling,
  } = props;

  // hooks
  const { elementRef, elementWidth } = useElementSize();

  const hasImages = listing.images && listing.images.length > 0;
  const hasVideos =
    listing.attributes?.publicData?.videos && listing.attributes.publicData.videos.length > 0;
  const firstImage = hasImages ? listing.images[0] : null;
  const videos = listing.attributes?.publicData?.videos || [];

  const posterImage = getPosterImage(listing, posterImageId, firstImage);

  const descriptionWidthMaybe =
    isTabletLayout || isMobileLayout
      ? {}
      : {
          style: {
            width: elementWidth,
          },
        };

  const mayEditForOrganization = !!organizationProfiles?.some(
    p => p?.id === organizationProfile?.id && roleHasPermission(p?.role, 'EDIT_ANY_SHOW')
  );
  const canEditListing = isOwnListing || mayEditForOrganization;

  const showRatings = getUserShowRatings(currentUser);
  const showRating = findUserShowRatingByListingId(showRatings, listing.id.uuid);
  const subscriptions = listing?.attributes?.metadata?.subscriptions?.length || 0;

  // title container
  const titleContainer = (
    <div className={css.titleContainer}>
      {organizationProfile?.id ? (
        <NamedLink
          className={css.organizationProfile}
          name="OrganizationProfilePage"
          params={{ id: organizationProfile.id }}
        >
          {organizationProfile.name}
        </NamedLink>
      ) : (
        <span className={css.organizationProfileLoadingText}>
          <FormattedMessage id="ListingPage.loadingOrganizationProfileText" />
        </span>
      )}
      <div className={css.listingData}>
        <div className={css.listingDataMainInfo}>
          <div className={css.listingImageMobile}>
            <AspectRatioWrapper width={5} height={7}>
              <ResponsiveImage
                loading="eager"
                rootClassName={css.rootForImage}
                alt={title}
                image={firstImage}
                variants={['portrait-crop2x', 'portrait-crop4x']}
              />
            </AspectRatioWrapper>
          </div>
          <div className={css.listingDataMainInfoColumn}>
            <h1 className={css.title}>{richTitle}</h1>
            <div className={css.topDescriptionWrapper} {...descriptionWidthMaybe}>
              <ExpandableBio
                className={css.topDescription}
                showMoreClassName={css.topDescriptionShowMore}
                bio={listing?.attributes?.description}
                maxLength={isMobileLayout ? DESCRIPTION_MAX_LENGTH_MOBILE : DESCRIPTION_MAX_LENGTH}
                onReadMore={onOpenDescriptionModal}
              />
            </div>
          </div>
        </div>

        <div className={css.topReviews} ref={elementRef}>
          <div className={css.topReviewsSection}>
            <span className={css.topReviewsHeading}>
              <FormattedMessage id="ListingPage.rating" />
            </span>
            <span className={css.topReviewsValue}>
              <Icon name="star" className={css.ratingStar} filled />
              <FormattedMessage
                id="ListingPage.ratingCount"
                values={{ count: averageReview, maxCount: MAX_AVERAGE_REVIEW }}
              />
            </span>
          </div>
          <div className={css.topReviewsSection}>
            <span className={css.topReviewsHeading}>
              <FormattedMessage id="ListingPage.yourRating" />
            </span>
            {showRating ? (
              <InlineTextButton
                type="button"
                className={css.topReviewsValue}
                onClick={onOpenShowRatingModal}
              >
                <Icon name="star" className={css.showRatingStar} filled />
                <FormattedMessage
                  id="ListingPage.ratingCount"
                  values={{ count: showRating.rating, maxCount: MAX_AVERAGE_REVIEW }}
                />
              </InlineTextButton>
            ) : (
              <InlineTextButton
                type="button"
                className={css.topReviewsValue}
                onClick={onOpenShowRatingModal}
              >
                <Icon name="star" className={css.rateStar} />
                <FormattedMessage id="ListingPage.rateText" />
              </InlineTextButton>
            )}
          </div>
          <div className={css.topReviewsSection}>
            <span className={css.topReviewsHeading}>
              <FormattedMessage id="ListingPage.userReviews" />
            </span>
            <InlineTextButton
              className={css.topReviewsValue}
              onClick={() => {
                const reviewsSection = document.getElementById('reviews');
                if (reviewsSection) {
                  const yOffset = isMobileLayout ? 60 : -72; // offset for sticky header
                  const y =
                    reviewsSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                  window.scrollTo({ top: y, behavior: 'smooth' });
                }
              }}
            >
              {reviews.length}
            </InlineTextButton>
          </div>
          <div className={css.topReviewsSection}>
            <span className={css.topReviewsHeading}>
              <FormattedMessage id="ListingPage.members" />
            </span>
            <span className={css.topReviewsValue}>{subscriptions}</span>
          </div>
        </div>
      </div>
    </div>
  );

  // view gallery button - showing total count of images and videos
  const totalMediaCount = (listing.images?.length || 0) + (videos?.length || 0);
  const hasMedia = hasImages || hasVideos;

  const viewPhotosButton = hasMedia ? (
    <InlineTextButton className={css.viewPhotos} onClick={handleViewPhotosClick}>
      <Icon name="image" className={css.galleryIcon} />
      <FormattedMessage id="ListingPage.viewGalleryButton" values={{ count: totalMediaCount }} />
    </InlineTextButton>
  ) : (
    <span className={css.viewPhotosMask}>
      <Icon name="image" className={css.galleryIcon} />
      <FormattedMessage id="ListingPage.viewGalleryButton" values={{ count: totalMediaCount }} />
    </span>
  );

  // author info container
  const authorInfo = (
    <div className={css.author}>
      {viewPhotosButton}
      <div className={css.authorActions}>
        {!isVariant && (
          <InlineTextButton className={css.authorAction} onClick={onOpenShareModal}>
            <Icon name="share" className={css.authorActionIcon} />
            <FormattedMessage id="ListingPage.shareButton" />
          </InlineTextButton>
        )}
        {canEditListing ? (
          <NamedLink
            className={css.authorAction}
            name="EditListingPage"
            params={editParams}
            to={{
              state: {
                from: `${location.pathname}${location.search}${location.hash}`,
              },
            }}
          >
            <Icon name="edit" className={classNames(css.authorActionIcon, css.editIcon)} />
            <FormattedMessage id={editMessageId} />
          </NamedLink>
        ) : null}
      </div>
    </div>
  );

  const sectionProps = {
    className: css.sectionImages,
  };

  return (
    <div {...sectionProps}>
      <div className={css.heroContentWrapper}>
        <div className={css.heroContent}>
          {authorInfo}
          <div className={css.listingInfo}>
            <div className={css.listingImage}>
              <AspectRatioWrapper
                className={css.listingImageAspectRatioWrapper}
                width={5}
                height={7}
              >
                <ResponsiveImage
                  loading="eager"
                  rootClassName={css.listingImageRoot}
                  alt={title}
                  image={posterImage}
                  variants={['portrait-crop2x', 'portrait-crop4x']}
                />
                <ButtonWatchlist listing={listing} />
              </AspectRatioWrapper>
            </div>
            {titleContainer}
          </div>
        </div>
        <Modal
          id="ListingPage.imageCarousel"
          scrollLayerClassName={css.carouselModalScrollLayer}
          containerClassName={css.carouselModalContainer}
          lightCloseButton
          isOpen={imageCarouselOpen}
          onClose={onImageCarouselClose}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <ImageCarousel
            images={listing.images || []}
            videos={videos}
            imageVariants={['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge']}
            activeImageId={activeImageIndex || 0}
          />
        </Modal>
      </div>
    </div>
  );
};

export default SectionImages;
